import { useCallback, useEffect, useState } from "react";
import { Button, DatePicker, Select, Space, Input, Tag, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import { PartnerLabel } from "../../components/common/DeletedTag";
import { getReconciliation } from "../../services/transactions.service";

function formatOmr(v) {
  return Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function KpiCard({ label, value, hint, tone }) {
  return (
    <div className="tx-kpi-card">
      <div className="tx-kpi-label">{label}</div>
      <div className={`tx-kpi-value ${tone || ""}`}>{value}</div>
      {hint ? <div className="tx-kpi-hint">{hint}</div> : null}
    </div>
  );
}

/**
 * Signed difference, coloured by which side is ahead.
 *
 * The value is App minus Admin (`ordersSystem - ordersUploaded`), so a positive
 * number means the app collected more than the admin CSV — green. A negative
 * number means the admin panel is ahead of the app — red, the case worth
 * chasing.
 */
function DiffCell({ value, format }) {
  const n = Number(value || 0);
  if (n === 0) return <span className="tx-variance-ok">—</span>;
  const shown = format ? format(Math.abs(n)) : Math.abs(n);
  return (
    <span className={n > 0 ? "tx-variance-pos" : "tx-variance-neg"}>
      {n > 0 ? "+" : "−"}
      {shown}
    </span>
  );
}

const EMPTY_KPIS = {
  ordersSystem: 0,
  ordersUploaded: 0,
  ordersDiffer: 0,
  collectedSystem: 0,
  collectedUploaded: 0,
  collectedDiffer: 0,
};

export default function ReconciliationTab() {
  const { options: projectOptions } = useSettingsDropdown("projects");
  const { options: fleetOptions } = useSettingsDropdown("fleet-vehicle-types");
  const { options: cityOptions } = useSettingsDropdown("city-zones");
  const { options: zoneOptions } = useSettingsDropdown("city-zones");

  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [project, setProject] = useState(undefined);
  const [fleetType, setFleetType] = useState(undefined);
  const [city, setCity] = useState(undefined);
  const [zone, setZone] = useState(undefined);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState(EMPTY_KPIS);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getReconciliation({
        page,
        per_page: 10,
        fromDate: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : undefined,
        toDate: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : undefined,
        project,
        fleetType,
        city,
        zone,
        search: search || undefined,
      });
      const data = res?.data || {};
      setKpis({ ...EMPTY_KPIS, ...(data.kpis || {}) });
      setRows(data.records || []);
      setTotal(data.pagination?.total_count || 0);
    } catch {
      message.error("Failed to load reconciliation");
    } finally {
      setLoading(false);
    }
  }, [page, dateRange, project, fleetType, city, zone, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      title: "Date",
      dataIndex: "transactionDateLabel",
      width: 120,
      render: (v) => v || "—",
    },
    {
      title: "Partner ID",
      dataIndex: "partnerId",
      width: 130,
      render: (v, r) => <PartnerLabel name={v || "—"} deleted={r.riderDeleted} />,
    },
    {
      title: "Partner Name",
      dataIndex: "partnerName",
      render: (v, r) => <PartnerLabel name={v} deleted={r.riderDeleted} />,
    },
    { title: "Work Contact No", dataIndex: "contactNo", render: (v) => v || "—" },
    { title: "Zone", dataIndex: "zoneName", render: (v) => v || "—" },
    {
      title: "Orders (App)",
      dataIndex: "ordersSystem",
      align: "right",
      render: (v) => v ?? 0,
    },
    {
      title: "Rider Deposited (App)",
      dataIndex: "collectedSystem",
      align: "right",
      render: formatOmr,
    },
    {
      title: "Orders (Admin)",
      dataIndex: "ordersUploaded",
      align: "right",
      render: (v) => v ?? 0,
    },
    {
      title: "Collected (Admin)",
      dataIndex: "collectedUploaded",
      align: "right",
      render: formatOmr,
    },
    {
      title: "Orders Differ",
      dataIndex: "ordersDiffer",
      align: "right",
      render: (v) => <DiffCell value={v} />,
    },
    {
      title: "Collected Differ",
      dataIndex: "collectedDiffer",
      align: "right",
      render: (v) => <DiffCell value={v} format={formatOmr} />,
    },
    {
      title: "Match",
      key: "matched",
      width: 110,
      render: (_, r) =>
        r.matched ? (
          <Tag color="green">Matched</Tag>
        ) : (
          <Tag color="red">Mismatch</Tag>
        ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Project"
          style={{ width: 160 }}
          options={projectOptions}
          value={project}
          onChange={(v) => {
            setPage(1);
            setProject(v);
          }}
        />
        <Select
          allowClear
          placeholder="Fleet Type"
          style={{ width: 160 }}
          options={fleetOptions}
          value={fleetType}
          onChange={(v) => {
            setPage(1);
            setFleetType(v);
          }}
        />
        <Select
          allowClear
          placeholder="City"
          style={{ width: 160 }}
          options={cityOptions}
          value={city}
          onChange={(v) => {
            setPage(1);
            setCity(v);
          }}
        />
        <Select
          allowClear
          placeholder="Zones"
          style={{ width: 160 }}
          options={zoneOptions}
          value={zone}
          onChange={(v) => {
            setPage(1);
            setZone(v);
          }}
        />
        <DatePicker.RangePicker
          value={dateRange}
          onChange={(v) => {
            setPage(1);
            setDateRange(v);
          }}
          format="DD-MM-YYYY"
          allowClear
          placeholder={["From date", "To date"]}
        />
        <Input.Search
          allowClear
          placeholder="Search..."
          style={{ width: 200 }}
          onSearch={(v) => {
            setPage(1);
            setSearch(v);
          }}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchData}>
          Refresh
        </Button>
      </Space>

      <div className="tx-kpi-row">
        <KpiCard label="Orders System" value={kpis.ordersSystem} hint="From app" />
        <KpiCard label="Orders Uploaded" value={kpis.ordersUploaded} hint="From admin" />
        <KpiCard
          label="Orders Differ"
          value={kpis.ordersDiffer}
          tone={kpis.ordersDiffer > 0 ? "diff" : "ok"}
        />
        <KpiCard
          label="Rider Deposited (System)"
          value={formatOmr(kpis.collectedSystem)}
          hint="From app"
        />
        <KpiCard
          label="Collected Uploaded"
          value={formatOmr(kpis.collectedUploaded)}
          hint="From admin"
        />
        <KpiCard
          label="Collected Differ"
          value={formatOmr(kpis.collectedDiffer)}
          tone={kpis.collectedDiffer > 0 ? "diff" : "ok"}
        />
      </div>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey="_id"
        pagination={{ current: page, pageSize: 10, total }}
        onPageChange={(p) => setPage(p)}
        scroll={{ x: 1400 }}
      />
    </div>
  );
}
